/**
 * Backend API Integration Endpoints
 * Base URL (e.g. http://api.ledryt.com) is supplied via VITE_API_BASE_URL
 */
export const ENDPOINTS = {
  SETTINGS: '/api/settings',
  SETTINGS_ID_FORMATS_PREVIEW: '/api/settings/id-formats/preview',
  ORGANIZATION: {
    ME: '/api/auth/me',
    UPLOAD_LOGO: '/api/organization/me/logo',
    LOGO: '/api/organization/me/logo',
  },
  ORG_TEMPLATES: {
    CATALOG: '/api/org-templates/catalog',
    RESOLVED: (templateKey: string) => `/api/org-templates/resolved/${templateKey}`,
    BASE: '/api/org-templates',
    BY_ID: (id: string) => `/api/org-templates/${id}`,
  },
  AUTH: {
    LOGIN: '/api/auth/login',
    RESET_PASSWORD: '/api/auth/reset-password',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    FORGOT_PASSWORD_VERIFY: '/api/auth/forgot-password/verify',
    FORGOT_PASSWORD_CONFIRM: '/api/auth/forgot-password/confirm',
    SUPERADMIN_LOGIN: '/api/auth/superadmin/login',
    REFRESH_TOKEN: '/api/auth/refresh',
    LOGOUT: '/api/auth/logout',
    CHANGE_PASSWORD: '/api/auth/change-password',
    ME: '/api/auth/me',
  },
  ROLES: {
    LIST: '/api/roles',
    CREATE: '/api/roles',
    GET_BY_ID: (id: string) => `/api/roles/${id}`,
    UPDATE: (id: string) => `/api/roles/${id}`,
    DELETE: (id: string) => `/api/roles/${id}`,
    PERMISSION_MATRIX: '/api/roles/permission-matrix',
    ASSIGN_BRANCH_ROLE: (userId: string) => `/api/users/${userId}/branch-role`,
  },
  DEPARTMENTS: {
    LIST: '/api/departments',
    CREATE: '/api/departments',
    GET_BY_ID: (id: string) => `/api/departments/${id}`,
    UPDATE: (id: string) => `/api/departments/${id}`,
    DELETE: (id: string) => `/api/departments/${id}`,
  },
  DESIGNATIONS: {
    LIST: '/api/designations',
    CREATE: '/api/designations',
    GET_BY_ID: (id: string) => `/api/designations/${id}`,
    UPDATE: (id: string) => `/api/designations/${id}`,
    DELETE: (id: string) => `/api/designations/${id}`,
  },
  STAFF: {
    LIST: '/api/staff',
    TEACHERS: '/api/staff/teachers',
    GET_BY_ID: (id: string) => `/api/staff/${id}`,
    CLASSES: '/api/staff/classes',
    CREATE: '/api/staff',
    UPDATE: (id: string) => `/api/staff/${id}`,
    DELETE: (id: string) => `/api/staff/${id}`,
  },
  DASHBOARD: {
    ADMIN: '/api/dashboard/admin',
    STUDENT: '/api/dashboard/student',
    STAFF: '/api/dashboard/staff',
    KPIS: '/api/dashboard/kpis',
    KPIS_STUDENT: '/api/dashboard/kpis/student',
    KPIS_STAFF: '/api/dashboard/kpis/staff',
    KPIS_SEAT: '/api/dashboard/kpis/seat',
    KPIS_ACADEMIC: '/api/dashboard/kpis/academic',
    KPIS_SUBJECT: '/api/dashboard/kpis/subject',
    KPIS_USER: '/api/dashboard/kpis/user',
    KPIS_ROLE: '/api/dashboard/kpis/role',
    KPIS_DEPARTMENT: '/api/dashboard/kpis/department',
    KPIS_DESIGNATION: '/api/dashboard/kpis/designation',
    KPIS_TEACHER: '/api/dashboard/kpis/teacher',
  },
  ADMIN: {
    DASHBOARD: '/api/dashboard/admin',
    DASHBOARD_STUDENT: '/api/dashboard/student',
    DASHBOARD_STAFF: '/api/dashboard/staff',
    DASHBOARD_KPI: '/api/admin/dashboard/kpi',
    DASHBOARD_STUDENT_SNAPSHOT: '/api/admin/dashboard/student-snapshot',
    DASHBOARD_DEPARTMENT_OVERVIEW: '/api/admin/dashboard/department-overview',
    DASHBOARD_TEACHER_SNAPSHOT: '/api/admin/dashboard/teacher-snapshot',
    DASHBOARD_ACADEMIC_PERFORMANCE: '/api/admin/dashboard/academic-performance',
    DASHBOARD_PENDING_APPROVAL: '/api/admin/dashboard/pending-approvals',
    DASHBOARD_RECENT_COMMUNITY: '/api/admin/dashboard/recent-community',
    DASHBOARD_FEES_STATUS: '/api/admin/dashboard/fees-status',
    DASHBOARD_ATTENDANCE_ANALYTICS: '/api/admin/dashboard/attendance-analytics',
    DASHBOARD_RECENT_ACTIVITY: '/api/admin/dashboard/recent-activity',
    DASHBOARD_SCHEDULE_AND_LEAVE: '/api/admin/dashboard/schedule-and-leave',
  },
  ADMISSIONS: {
    DASHBOARD_KPI: '/api/admissions/dashboard/kpi',
    APPLICATION_LIST: '/api/admissions/applications',
  },
  ASSIGNMENTS: {
    CREATE: '/api/assignments',
    LIST: '/api/assignments',
    SUBMISSION_TYPES: '/api/assignments/submission-types',
    GET_BY_ID: (id: string) => `/api/assignments/${id}`,
    CLOSE: (id: string) => `/api/assignments/${id}/close`,
    PUBLISH: (id: string) => `/api/assignments/${id}/publish`,
    DELETE: (id: string) => `/api/assignments/${id}`,
    SUBMISSIONS: (id: string) => `/api/assignments/${id}/submissions`,
    GRADE_SUBMISSION: (id: string, studentId: string) => `/api/assignments/${id}/submissions/${studentId}/grade`,
    DELETE_ATTACHMENT: (id: string, attachmentId: string) => `/api/assignments/${id}/attachments/${attachmentId}`,
    NUDGE: (id: string) => `/api/assignments/${id}/nudge`,
  },
  STUDENT: {
    ASSIGNMENTS: '/api/student/assignments',
    SUBMIT_ASSIGNMENT: '/api/student/assignments/submit',
  },
  SEAT_MANAGEMENT: {
    GRADES: '/api/grades/grades',
    GRADE_BY_ID: (id: string) => `/api/grades/grades/${id}`,
    GRADE_CRITERIA: '/api/grade-criteria',
    GRADE_CRITERIA_BY_ID: (id: string) => `/api/grade-criteria/${id}`,
    SECTIONS_BY_GRADE: (gradeId: string) => `/api/grades/${gradeId}/sections`,
    BULK_SECTIONS: (gradeId: string) => `/api/grades/${gradeId}/sections/bulk`,
    SECTIONS: '/api/sections',
    SECTION_BY_ID: (id: string) => `/api/sections/${id}`,
    LOOKUP_VALUES: (typeCode: string) => `/api/lookup-values?typeCode=${typeCode}`,
  },
  ACADEMICS: {
    GET_CURRENT_SESSION: '/api/academic-sessions/current',
    CREATE_SESSION: '/api/academic-sessions',
    GET_YEARS: '/api/academic-sessions',
    GET_YEAR_DETAILS: (id: string) => `/api/academic-sessions/${id}`,
    GET_STRUCTURE: (id: string) => `/api/academic-sessions/${id}/structure`,
    DOWNLOAD_REPORT: (id: string) => `/api/academic-sessions/${id}/report.pdf`,
    UPDATE_YEAR: (id: string) => `/api/academic-sessions/${id}`,
    DELETE_SESSION: (id: string) => `/api/academic-sessions/${id}`,
    CREATE_TERM: '/api/terms',
    GET_TERM_TYPES: '/api/lookup-values?typeCode=TERM_TYPE',
    GET_ACADEMIC_PERIOD_SYSTEM_TYPES: '/api/lookup-values?typeCode=ACADEMIC_PERIOD_SYSTEM',
    GET_ACADEMIC_SESSION_STATUS_TYPES: '/api/lookup-values?typeCode=ACADEMIC_SESSION_STATUS',
    CREATE_HOLIDAY: '/api/holidays',
    UPDATE_HOLIDAY: (id: string) => `/api/holidays/${id}`,
    DELETE_HOLIDAY: (id: string) => `/api/holidays/${id}`,
    GET_HOLIDAY_TYPES: '/api/lookup-values?typeCode=HOLIDAY_TYPE',
    GET_SEASONAL_BREAK_TYPES: '/api/lookup-values?typeCode=BREAK_TYPE',
    GET_IMPACT_GROUPS: '/api/lookup-values?typeCode=IMPACT_GROUP',
    CREATE_SEASONAL_BREAK: '/api/seasonal-breaks',
    UPDATE_SEASONAL_BREAK: (id: string) => `/api/seasonal-breaks/${id}`,
    DELETE_SEASONAL_BREAK: (id: string) => `/api/seasonal-breaks/${id}`,
  },
  SUBJECTS: {
    CREATE: '/api/subjects',
    LIST: '/api/subjects',
    SUBJECT_TYPES: '/api/subjects/subject-types',
    CATEGORIES: '/api/subjects/categories',
    BY_ID: (id: string) => `/api/subjects/${id}`,
    ALLOCATIONS: (id: string) => `/api/subjects/${id}/allocations`,
    ALLOCATE: '/api/subjects/allocate',
  },
  STUDENTS: {
    LIST: '/api/students',
    GET_BY_ID: (id: string) => `/api/students/${id}`,
    STAFF_VIEW: (id: string) => `/api/students/${id}/staff-view`,
    CREATE: '/api/students',
    UPDATE: (id: string) => `/api/students/${id}`,
    DELETE: (id: string) => `/api/students/${id}`,
    TRANSFER: (id: string) => `/api/students/${id}/transfers`,
    PROMOTIONS: {
      CANDIDATES: '/api/students/promotions/candidates',
      EXECUTE: '/api/students/promotions/execute',
    },
  },
  LOOKUP_VALUES: (typeCode: string) => `/api/lookup-values?typeCode=${typeCode}`,

  IMPORTS: {
    UPLOAD: '/api/imports',
    COMMIT: (importId: string) => `/api/imports/${importId}/commit`,
    TEMPLATE: '/api/imports/template',
    GET_BY_ID: (importId: string) => `/api/imports/${importId}`,
  },
  DOCUMENT_VERIFICATIONS: {
    QUEUE: '/api/document-verifications',
    HEADER: (studentId: string) => `/api/document-verifications/${studentId}`,
    VERIFY: (studentId: string) => `/api/document-verifications/${studentId}/verify`,
    REUPLOAD_REQUESTS: (studentId: string) => `/api/document-verifications/${studentId}/reupload-requests`,
    DOCUMENTS: (studentId: string) => `/api/document-verifications/${studentId}/documents`,
    UPLOAD_DOCUMENT: (studentId: string) => `/api/document-verifications/${studentId}/documents`,
    DOWNLOAD_DOCUMENT: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}/download`,
    DELETE_DOCUMENT: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}`,
    GET_DOCUMENT: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}`,
    LOG_DECISION: (studentId: string, documentId: string) => `/api/document-verifications/${studentId}/documents/${documentId}/logs`,
  },
  ACADEMIC_SESSIONS: {
    GET_CURRENT: '/api/academic-sessions/current',
    LIST: '/api/academic-sessions',
    OPTIONS: '/api/academic-sessions/options',
    SESSIONS: '/api/academic-sessions',
  },
  FEE_REPORTS: {
    OVERVIEW: '/api/fee-reports/overview',
    TRANSACTIONS: '/api/fee-reports/transactions',
    CLASS: '/api/fee-reports/class',
    REVENUE: '/api/fee-reports/revenue',
    PENDING_DUES: '/api/fee-reports/pending-dues',
    INVOICE_RECONCILIATION: '/api/fee-reports/invoice-reconciliation',
    STUDENT_WISE: (studentId: string) => `/api/fee-reports/student/${encodeURIComponent(studentId)}`,
    STUDENT_WISE_PDF: (studentId: string) => `/api/fee-reports/student/${encodeURIComponent(studentId)}/pdf`,
  },
  USERS: {
    LIST: '/api/users',
    STUDENTS: '/api/users/students',
    LOOKUP: '/api/users/lookup',
    ENABLE_LOGIN: '/api/users',
    GET_BY_ID: (id: string) => `/api/users/${id}`,
  },
  COMMUNICATION: {
    DASHBOARD: '/api/communications/dashboard',
    MESSAGES: '/api/communications',
    CREATE: '/api/communications',
    DELETE: (id: string) => `/api/communications/${id}`,
    UPDATE: (id: string) => `/api/communications/${id}`,
    GET_BY_ID: (id: string) => `/api/communications/${id}`,
    TARGET_TYPES: '/api/communications/target-types',
    CHANNELS: '/api/communications/channels',
    SMS_TEMPLATES: '/api/communications/sms-templates',
    SEND: (id: string) => `/api/communications/${id}/send`,
    STATUS: (id: string) => `/api/communications/${id}/status`,
    TARGET_USER_COUNT: '/api/communications/target-user-count',
    CANCEL_SCHEDULE: (id: string) => `/api/communications/${id}/cancel-schedule`,
    ATTACHMENTS: '/api/communications/attachments',
    DELETE_ATTACHMENT: (id: string, attachmentId: string) => `/api/communications/${id}/attachments/${attachmentId}`,
    FAILED_RECIPIENTS: (id: string) => `/api/communications/${id}/failed-recipients`,
    RETRY_FAILED: (id: string) => `/api/communications/${id}/retry-failed`,
  },
  GRADES: {
    LIST: '/api/grades/grades',
    CREATE: '/api/grades/grades',
    GET_BY_ID: (id: string) => `/api/grades/grades/${id}`,
    UPDATE: (id: string) => `/api/grades/grades/${id}`,
    DELETE: (id: string) => `/api/grades/grades/${id}`,
    OCCUPANCY: '/api/grades/occupancy',
    DIRECTORY: '/api/grades/directory',
    OVERVIEW: (gradeId: string) => `/api/grades/${gradeId}/overview`,
    SECTIONS: {
      LIST: (gradeId: string) => `/api/grades/${gradeId}/sections`,
      ASSIGN_CLASS_TEACHER: (gradeId: string, sectionId: string) => `/api/grades/${gradeId}/sections/${sectionId}/class-teacher`,
    },
  },
  DEVICE_TOKENS: {
    REGISTER: '/api/device-tokens',
  },
  ATTENDANCE: {
    CLASSES: '/api/attendance/classes',
    SECTION: (sectionId: string) => `/api/attendance/section/${sectionId}`,
    MARK: '/api/attendance/mark',
    MARK_GRADEWISE: '/api/attendance/mark-gradewise',
    GRADEWISE: '/api/attendance/gradewise',
    STUDENT_MONTHLY: (studentId: string) => `/api/attendance/student/${studentId}/monthly`,
    LOGS: '/api/attendance/logs',
  },
  NOTICES: {
    LIST: '/api/notices',
    CREATE: '/api/notices',
    UPDATE: (id: string) => `/api/notices/${id}`,
    DELETE: (id: string) => `/api/notices/${id}`,
    DETAILS: (id: string) => `/api/notices/${id}`,
    PUBLISH: (id: string) => `/api/notices/${id}/publish`,
    VIEWS: (id: string) => `/api/notices/${id}/views`,
    DELETE_ATTACHMENT: (id: string, attachmentId: string) => `/api/notices/${id}/attachments/${attachmentId}`,
  },
  LESSON_PLANS: {
    OPTIONS: '/api/lesson-plans/options',
    STAGE_ATTACHMENT: '/api/lesson-plans/attachments/staging',
    LIST: '/api/lesson-plans',
    CREATE: '/api/lesson-plans',
    COVERAGE: (id: string) => `/api/lesson-plans/${id}/coverage`,
    DETAIL: (id: string) => `/api/lesson-plans/${id}`,
    UPDATE: (id: string) => `/api/lesson-plans/${id}`,
    DELETE: (id: string) => `/api/lesson-plans/${id}`,
    PUBLISH: (id: string) => `/api/lesson-plans/${id}/publish`,
    DUPLICATE: (id: string) => `/api/lesson-plans/${id}/duplicate`,
    SPLIT_AND_UPDATE: (id: string) => `/api/lesson-plans/${id}/split-and-update`,
    ADD_CHAPTER: (id: string) => `/api/lesson-plans/${id}/chapters`,
    UPDATE_CHAPTER: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}`,
    DELETE_CHAPTER: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}`,
    ADD_TOPIC: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/topics`,
    UPDATE_TOPIC: (id: string, chapterId: string, topicId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/topics/${topicId}`,
    DELETE_TOPIC: (id: string, chapterId: string, topicId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/topics/${topicId}`,
    CHAPTER_COMPLETION: (id: string, chapterId: string) => `/api/lesson-plans/${id}/chapters/${chapterId}/completion`,
  },
  NOTIFICATIONS: {
    UNREAD_COUNT: '/api/notifications/unread-count',
    LIST: '/api/notifications',
    MARK_ALL_READ: '/api/notifications/mark-all-read',
    MARK_READ: (id: string) => `/api/notifications/${id}/read`,
  },
  STAFF_PORTAL: {
    MESSAGES: '/api/staff-portal/messages',
    MESSAGE_BY_ID: (id: string) => `/api/staff-portal/messages/${id}`,
  },
} as const
